import { editorialScopeKey, getSettledEditorialCapabilities, isEditorialResource } from 'app/models/editorialCapabilities';
import { USER_ROLES } from 'app/models/user/user.constants';
import { loadEditorialAccessMe } from 'app/services/editorialAccess.service';
import { confirmEditorialRequestScope, confirmEditorialRequestSession } from 'app/config/editorialRequestSession.config';
import { canOpenEditorialContent, canOpenEditorialProgram } from 'app/services/editorialPrograms.service';

export const EDITORIAL_MUTATION_CANCELLED = Object.freeze({ cancelled: true, error: null });

// Un lote autorizado una sola vez se cancela por dos vías: la autorización previa
// (el runner devuelve el objeto cancelado) o cualquiera de sus peticiones.
export function isCancelledEditorialBatch(result) {
  if (result?.cancelled) return true;
  return Array.isArray(result) && result.some((response) => response?.cancelled);
}

export async function authorizeEditorialMutation(access, resources, signal, onAccess) {
  if (signal?.aborted || !getSettledEditorialCapabilities(access, resources).canWrite) return false;
  // La revalidación contra /me solo aporta algo a quien puede estar restringido.
  if (access.role !== USER_ROLES.publisher) return true;
  const data = await loadEditorialAccessMe(signal);
  if (!data || signal?.aborted) return false;
  onAccess?.(data);
  const current = { ...access, data };
  if (!getSettledEditorialCapabilities(current, resources).canWrite) return false;
  // Sin esto la escritura autorizada moría en el interceptor, que sigue viendo la sesión pausada por la
  // revalidación en curso.
  confirmEditorialRequestSession(data);
  if (access.role === USER_ROLES.publisher && data.restricted) {
    if (!resources.length || resources.some((resource) => !isEditorialResource(resource))) return false;
    const allowed = await Promise.all(
      resources.map((resource) => (resource.type === 'program' ? canOpenEditorialProgram : canOpenEditorialContent)(resource.guid, signal))
    );
    if (allowed.some((value) => value !== true)) return false;
  }
  if (signal?.aborted) return false;
  confirmEditorialRequestScope();
  return true;
}

// Se compara el ámbito, no la identidad del objeto: el provider crea un estado nuevo en cada
// revalidación por foco y eso no es un cambio de permiso.
function isCurrentMutation(getSnapshot, snapshot, signal) {
  const current = getSnapshot();
  if (signal?.aborted) return false;
  if (editorialScopeKey(current?.access, current?.resources) !== editorialScopeKey(snapshot?.access, snapshot?.resources)) return false;
  return getSettledEditorialCapabilities(current?.access, current?.resources).canWrite;
}

export async function runEditorialMutation({ getSnapshot, operation, signal, authorize = authorizeEditorialMutation, onAccess, onDenied }) {
  const snapshot = getSnapshot();
  if (signal?.aborted || !getSettledEditorialCapabilities(snapshot?.access, snapshot?.resources).canWrite) return EDITORIAL_MUTATION_CANCELLED;
  try {
    if (!(await authorize(snapshot.access, snapshot.resources, signal, onAccess))) {
      if (!signal?.aborted) onDenied?.();
      return EDITORIAL_MUTATION_CANCELLED;
    }
  } catch {
    if (!signal?.aborted) onDenied?.();
    return EDITORIAL_MUTATION_CANCELLED;
  }
  if (!isCurrentMutation(getSnapshot, snapshot, signal)) return EDITORIAL_MUTATION_CANCELLED;
  const response = await operation();
  // La petición ya salió: devolverla como cancelada haría que el consumidor revirtiese en pantalla algo
  // que el servidor ha aplicado. Solo un ciclo de vida abortado —desmontaje o cambio real de ámbito—
  // corta aquí, y entonces no queda nadie a quien revertir.
  return signal?.aborted ? EDITORIAL_MUTATION_CANCELLED : response;
}
